import { useLayoutEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// ---------------------------------------------------------------------------
// ScrollToTop — a new page opens at its top, or at the section it names.
//
// A browser resets the scroll position on a full page load. A <BrowserRouter>
// navigation is not a page load: it swaps the route's component and leaves the
// window where it was. So a link clicked half-way down one page opened the next
// page half-way down too, and a refresh "fixed" it because a refresh IS a page
// load. (<ScrollRestoration> would do this, but it only works under a data
// router — createBrowserRouter — and this app uses <BrowserRouter>.)
//
// A URL with a #hash is the visitor asking for a section, and the router does
// not scroll to one either — so this does, for the same reason. It has to WAIT
// for it: the pages are lazy-loaded, so on a link from another page the target
// does not exist yet when this runs. It looks once per frame for up to
// HASH_WAIT_MS, then gives up and opens the page at its top rather than leave
// the reader wherever the previous page was. The header clearance is the CSS
// `scroll-margin-top` on the target, not a number here.
//
// Left alone on purpose:
//   · POP — Back and Forward. The visitor expects to return to where they
//     were, and the browser's own scroll restoration handles that. This also
//     covers a plain <a href="#x"> on the same page: the browser scrolls it
//     natively and the router hears it as a POP.
//   · a query-string-only change on the same page (a filter, a tab,
//     pagination), which is why the dependency list is pathname + hash and not
//     the whole location.
// ---------------------------------------------------------------------------

const HASH_WAIT_MS = 2000;

export default function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    if (navigationType === 'POP') return;
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    const id = decodeURIComponent(hash.slice(1));
    const deadline = Date.now() + HASH_WAIT_MS;
    let frame = 0;
    const seek = () => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView();
      } else if (Date.now() < deadline) {
        frame = requestAnimationFrame(seek);
      } else {
        window.scrollTo(0, 0);
      }
    };
    seek();
    return () => cancelAnimationFrame(frame);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, hash]);

  return null;
}
